import { countries } from '@/consts/languageAccordance';
import {
    ThemeVariables,
    variables as defaultVariables,
    colors as defaultColors,
} from '../mpanels/variables';
import { addOpacityToThemeColor } from '@/utils/styles/addOpacity';

const colors = {
    ...defaultColors,
    lightGray: 'rgba(210, 210, 210, 1)',
    nearWhiteBlue: 'rgba(250, 253, 255, 1)',
    success: 'rgba(135, 173, 64, 1)',
    info: 'rgba(138, 59, 135, 1)',
    warning: 'rgba(255, 164, 61, 1)',
    error: 'rgba(231, 64, 56, 1)',
};

const palette = {
    ...defaultVariables.colors.palette,
    primary: {
        main: 'rgba(135, 173, 64, 1)',
        light: 'rgba(148, 196, 66, 1)',
        dark: 'rgba(94, 125, 56, 1)',
        dark2: 'rgba(77, 102, 46, 1)',
        dark3: 'rgba(38, 69, 31, 1)',
        dark4: 'rgba(18, 45, 2, 1)',
        contrastText: colors.white,
    },
    secondary: {
        main: 'rgba(163, 69, 153, 1)',
        light: 'rgba(181, 84, 158, 1)',
        dark: 'rgba(138, 59, 135, 1)',
        dark2: 'rgba(115, 54, 117, 1)',
        dark3: 'rgba(92, 43, 97, 1)',
        dark4: 'rgba(48, 23, 51, 1)',
        contrastText: colors.white,
    },
    tertiary: {
        main: 'rgba(10, 133, 199, 1)',
        light: 'rgba(17, 167, 219, 1)',
        dark: 'rgba(8, 110, 153, 1)',
        dark2: 'rgba(8, 92, 128, 1)',
        dark3: 'rgba(10, 69, 87, 1)',
        dark4: 'rgba(5, 38, 51, 1)',
        contrastText: colors.white,
    },
    background: {
        default: colors.white,
    },
    text: {
        primary: 'rgba(92, 94, 97, 1)',
        light: 'rgba(120, 120, 120, 1)',
        dark: 'rgba(59, 59, 59, 1)',
        dark2: 'rgba(13, 13, 13, 1)',
    },
    gray: {
        main: 'rgba(240, 240, 240, 1)',
        light: 'rgba(245, 246, 247, 1)',
        dark: 'rgba(225, 225, 225, 1)',
        dark2: 'rgba(210, 210, 210, 1)',
        dark3: 'rgba(181, 181, 181, 1)',
        dark4: 'rgba(140, 140, 140, 1)',
    },
    success: {
        main: colors.success,
    },
    info: {
        main: colors.info,
    },
    warning: {
        main: colors.warning,
    },
    error: {
        main: colors.error,
    },
};

const fonts = {
    primary: '"Roboto Flex", sans-serif',
    secondary: '"Roboto Flex", sans-serif',
    additional: '"Roboto Flex", sans-serif',
    body: {
        fontSize: '1rem',
    },
};

type AGCThemeVariables = ThemeVariables & {
    colors: {
        palette: ThemeVariables['colors']['palette'] & typeof palette;
    };
};

export const variables: AGCThemeVariables = {
    themeName: 'agc',
    sortOrder: {
        ...defaultVariables.sortOrder,
        countriesSelectOptions: [
            [String(countries['us'].phraseId)],
            [String(countries['gb'].phraseId)],
            [
                String(countries['de'].phraseId),
                String(countries['fr'].phraseId),
                String(countries['es'].phraseId),
                String(countries['it'].phraseId),
            ],
            ['*'],
        ],
    },
    colors: {
        palette,
        links: {
            main: palette.tertiary.dark,
            hover: palette.tertiary.main,
            active: palette.tertiary.dark3,
            disabled: palette.gray.dark,
        },
        error: {
            main: palette.error.main,
        },
        other: {
            badge: {
                topMenu: {
                    color: 'white',
                    background: palette.warning.main,
                },
            },
            chip: {
                default: {
                    background: addOpacityToThemeColor(palette.primary.main, 0.1),
                    text: palette.primary.main,
                },
                methodologyType: {
                    telephone: {
                        background: colors.nearWhiteBlue,
                        text: 'rgba(106, 181, 221, 1)',
                    },
                    online: {
                        background: colors.nearWhiteBlue,
                        text: 'rgba(58, 157, 210, 1)',
                    },
                    inPerson: {
                        background: colors.nearWhiteBlue,
                        text: 'rgba(8, 106, 159, 1)',
                    },
                },
            },
            table: {
                evenRowHighlight: palette.gray.light,
                headerText: palette.gray.dark4,
                hoverHighlight: palette.gray.light,
                text: palette.text.primary,
                hoverText: palette.tertiary.main,
            },
            background: {
                ...defaultVariables.colors.other.background,
                header: colors.white,
                topMenu: colors.white,
                footer: palette.gray.light,
                highlight: palette.gray.main,
                highlightHover: palette.gray.main,
                success: 'rgba(244, 252, 216, 1)',
                warning: 'rgba(254, 248, 207, 1)',
                divideline: palette.gray.main,
                preloader: palette.primary.dark2,
            },
            text: {
                ...defaultVariables.colors.other.text,
                header: palette.text.primary,
                registrationDescription: palette.text.dark,
                footer: palette.text.primary,
                copyrightText: palette.gray.dark4,
                lightGray: colors.lightGray,
                gray: palette.text.primary,
                success: palette.success.main,
                warning: palette.warning.main,
                error: palette.error.main,
                info: palette.info.main,
            },
            inputs: {
                default: palette.gray.dark4,
                hover: palette.success.main,
                focus: palette.primary.dark3,
            },
            icon: {
                stroke: {
                    primary: palette.primary.dark2,
                    secondary: palette.primary.dark2,
                    tertiary: palette.primary.dark2,
                },
                background: {
                    primary: 'transparent',
                    secondary: 'transparent',
                    tertiary: 'transparent',
                },
            },
            notifications: {
                success: palette.success.main,
                info: palette.info.main,
                warning: palette.warning.main,
                error: palette.error.main,
            },
        },
    },
    fonts,
    borderRadius: {
        primary: '10px',
        secondary: '20px',
        tertiary: '14px',
        topMenu: '20px',
        mobileTopMenu: '0',
    },
    padding: {
        ...defaultVariables.padding,
        inputsDefault: '8.5px 12px',
        tableCellPadding: '16px',
        elementPadding: '0 0 12px 0',
    },
    margin: {
        ...defaultVariables.margin,
    },
    boxShadows: {
        popoverShadow: `0px 8px 16px -9px rgba(119, 119, 119, 0.32)`,
        tableShadow: 'inherit',
        tableSelectedShadow: `inset 0 2px 4px ${addOpacityToThemeColor(colors.black, 0.06)}`,
        headerShadow: `0px 1px 1px 0px ${palette.gray.main}`,
        buttonShadow: 'inherit',
        inputShadow: '0px 0px 0px 2px rgba(141, 197, 64, 0.50)',
        tooltipShadow: `0px 4px 4px 0px ${addOpacityToThemeColor(palette.text.primary, 0.16)}`,
        descriptiveShadow: 'inherit',
    },
    layout: {
        helpCentreInsteadFaq: true,
        home: {
            hasBackgroundImage: false,
            pageTitle: {
                textAlign: 'left',
                position: 'inline',
            },
            description: {
                visible: true,
                position: 'top',
            },
            picBlock: {
                visible: true,
                position: 'top',
                src: '/images/themes/agc/registration-intro-pic.png',
            },
            iconsBlock: {
                visible: true,
                position: 'bottom',
                inlineIcons: false,
                paperBox: false,
            },
            registrationForm: {
                fullwidthForm: false,
                showStepTitle: false,
                stepperOrientation: 'horizontal',
            },
        },
        footer: {
            fullWidth: false,
            logoPosition: 'right',
            themeColorSocialIcons: false,
            companyLinkVisible: false,
            inlineFollowUs: true,
            hasFollowUsTitle: false,
        },
    },
    cookie: {
        button: {
            hover: {
                color: 'white',
                backgroundColor: palette.primary.main,
            },
            borderRadius: '20px',
            shadow: 'initial',
        },
        toggle: {
            initial: {
                selected: {
                    toggleColor: palette.primary.dark,
                    backgroundColor: addOpacityToThemeColor(palette.primary.dark, 0.5),
                },
                unselected: {
                    toggleColor: 'white',
                    backgroundColor: addOpacityToThemeColor(palette.gray.dark4, 0.5),
                },
            },
            disabled: {
                toggleColor: palette.gray.main,
                backgroundColor: palette.gray.dark2,
            },
        },
    },
    typography: {
        fontFamily: fonts.primary,
        h1: {
            color: palette.primary.dark2,
            fontFamily: fonts.secondary,
            fontSize: '2.375rem', // 38px
            fontWeight: '600',
        },
        h2: {
            color: palette.primary.dark2,
            fontFamily: fonts.secondary,
            fontSize: '2rem', // 32px
            fontWeight: '600',
        },
        h3: {
            color: palette.primary.dark2,
            fontFamily: fonts.secondary,
            fontSize: '1.5rem', // 24px
            fontWeight: '600',
        },
        h4: {
            color: palette.primary.dark2,
            fontSize: '1.125rem', // 18px
            fontFamily: fonts.secondary,
            fontWeight: '600',
        },
        h5: {
            color: palette.primary.dark2,
            fontSize: '1rem', // 16px
            fontFamily: fonts.secondary,
            fontWeight: '600',
        },
        h6: {
            color: palette.primary.dark2,
            fontSize: '1rem', // 16px
            fontFamily: fonts.secondary,
            fontWeight: '600',
        },
        body1: {
            fontSize: '1rem', // 16px
            fontFamily: fonts.primary,
        },
        body2: {
            fontSize: '1rem', // 16px
            fontFamily: fonts.secondary,
            fontWeight: '400',
        },
        subtitle1: {
            fontSize: '1rem', // 16px
            fontFamily: fonts.secondary,
            fontWeight: '400',
        },
        subtitle2: {
            fontSize: '1rem', // 14px
            fontFamily: fonts.secondary,
            fontWeight: '400',
        },
        subtitle3: {
            fontSize: '0.875rem', // 14px
            fontFamily: fonts.secondary,
        },
        greyLabel: {
            fontSize: '0.875rem', // 14px
            fontFamily: fonts.secondary,
        },
        link: {
            textDecoration: 'none',
            '&:hover': {
                textDecoration: 'underline',
            },
            '&:active': {
                textDecoration: 'inherit',
            },
            '&:disabled': {
                textDecoration: 'inherit',
            },
        },
    },
};
